import { Modal as BootstrapModal } from 'react-bootstrap';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from 'app/components/ui';
import { i18n } from 'app/utils/i18n';
import { TagContext } from 'app/state/contexts/TagContext';
import {
  AvailableDimension,
  AvailableHypercube,
  AvailableMember,
  UUID,
  useCreateExtensionMember,
  useExtensions,
  useTaxonomyLanguages,
  useUpdateExtension,
} from 'app/api/tag-queries';

interface ExtensionMemberModalProps {
  show: boolean;
  onClose: () => void;
  hypercube: AvailableHypercube;
  axis: AvailableDimension;
  documentSuperId: number;
  taxonomyId: UUID;
  editMember?: AvailableMember;
}

interface FlatMember {
  conceptId: UUID;
  qname: string;
  label: string;
  isDefault: boolean;
}

function localName(qname: string): string {
  const colon = qname.indexOf(':');
  return colon >= 0 ? qname.slice(colon + 1) : qname;
}

function flattenMembers(axis: AvailableDimension, lang: string): FlatMember[] {
  const seen = new Set<UUID>();
  const out: FlatMember[] = [];
  const labelOf = (member: AvailableMember): string => {
    const flat = member.labels as unknown as Record<string, string | undefined> | undefined;
    return flat?.[lang] ?? flat?.en ?? flat?.fallback ?? member.id;
  };
  function walk(member: AvailableMember): void {
    if (member.conceptId && !seen.has(member.conceptId)) {
      seen.add(member.conceptId);
      out.push({
        conceptId: member.conceptId,
        qname: member.id,
        label: labelOf(member),
        isDefault: axis.defaultMemberId !== null && member.id === axis.defaultMemberId,
      });
    }
    for (const c of member.children) walk(c);
  }
  for (const domain of axis.domains) walk(domain.members);
  return out;
}

function treeLabels(member: AvailableMember | undefined): Record<string, string> {
  if (!member) return {};
  const out: Record<string, string> = {};
  const flat = member.labels as unknown as Record<string, string | undefined>;
  for (const lang of Object.keys(flat ?? {})) {
    if (lang === 'fallback') continue;
    const text = flat[lang];
    if (text) out[lang] = text;
  }
  return out;
}

export const ExtensionMemberModal: React.FC<ExtensionMemberModalProps> = ({ show, onClose, axis, documentSuperId, taxonomyId, editMember }) => {
  const { tagLanguage } = useContext(TagContext);
  const { data: languages } = useTaxonomyLanguages(documentSuperId, taxonomyId);
  const { data: extensions } = useExtensions(documentSuperId, taxonomyId);
  const { mutate: createMember, isLoading: isCreating } = useCreateExtensionMember();
  const { mutate: updateMember, isLoading: isUpdating } = useUpdateExtension();
  const isLoading = isCreating || isUpdating;
  const isEdit = !!editMember;

  const summary = useMemo(() => extensions?.find((e) => e.id === editMember?.conceptId), [extensions, editMember]);

  const flatMembers = useMemo(() => flattenMembers(axis, tagLanguage), [axis, tagLanguage]);
  const parentMembers = useMemo(() => flatMembers.filter((m) => m.conceptId !== editMember?.conceptId), [flatMembers, editMember]);

  const [name, setName] = useState(editMember ? localName(editMember.id) : '');
  const [parentId, setParentId] = useState<UUID | ''>('');
  const [labels, setLabels] = useState<Record<string, string>>(() => treeLabels(editMember));

  const summaryApplied = useRef(false);
  useEffect(() => {
    if (!summary || summaryApplied.current) return;
    summaryApplied.current = true;
    setParentId(summary.parentMemberConceptId ?? '');
    const fromSummary: Record<string, string> = {};
    for (const lang of Object.keys(summary.labels ?? {})) {
      const text = summary.labels[lang]?.label;
      if (text) fromSummary[lang] = text;
    }
    if (Object.keys(fromSummary).length > 0) setLabels(fromSummary);
  }, [summary]);

  const langs = languages?.length ? languages : ['en'];
  const hasLabel = langs.some((lang) => (labels[lang] ?? '').trim().length > 0);

  const reset = () => {
    setName('');
    setParentId('');
    setLabels({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = () => {
    if (!name || !parentId || !hasLabel) return;
    const labelsByLang: Record<string, Record<string, string>> = {};
    for (const lang of langs) {
      const text = labels[lang]?.trim();
      if (text) labelsByLang[lang] = { label: text };
    }
    if (editMember?.conceptId) {
      updateMember(
        {
          conceptId: editMember.conceptId,
          documentSuperId,
          taxonomyId,
          name: name.trim(),
          labels: labelsByLang,
          parentMemberConceptId: parentId,
        },
        { onSuccess: () => handleClose() }
      );
      return;
    }
    createMember(
      {
        documentSuperId,
        taxonomyId,
        name: name.trim(),
        labels: labelsByLang,
        axisConceptId: axis.conceptId as UUID,
        parentMemberConceptId: parentId,
      },
      {
        onSuccess: () => handleClose(),
      }
    );
  };

  const axisLabel = useMemo(() => {
    const flat = axis.labels as unknown as Record<string, string | undefined> | undefined;
    return flat?.[tagLanguage] ?? flat?.en ?? flat?.fallback ?? axis.id;
  }, [axis, tagLanguage]);

  const canSubmit = !isLoading && name.trim().length > 0 && parentId !== '' && hasLabel;

  return (
    <BootstrapModal show={show} onHide={handleClose} centered>
      <BootstrapModal.Header closeButton>
        <BootstrapModal.Title>{i18n(isEdit ? 'tag.dimensions.edit-member.title' : 'tag.dimensions.add-member.title')}</BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body>
        <div className="mb-3">
          <Typography variant="body-7" color="#717077">
            {i18n('tag.dimensions.add-member.axis')}
          </Typography>
          <Typography variant="body-5" fontWeight="semibold">
            {axisLabel}
          </Typography>
        </div>

        <div className="mb-3">
          <label className="form-label">
            <Typography variant="body-5">{i18n('tag.dimensions.add-member.name')}</Typography>
          </label>
          <input
            type="text"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="MyCustomMember"
            disabled={isLoading}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            <Typography variant="body-5">{i18n('tag.dimensions.add-member.parent')}</Typography>
          </label>
          <select className="form-select" value={parentId} onChange={(e) => setParentId(e.target.value as UUID)} disabled={isLoading}>
            <option value="">{i18n('tag.dimensions.add-member.parent-placeholder')}</option>
            {parentMembers.map((m) => (
              <option key={m.conceptId} value={m.conceptId}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {langs.map((lang) => (
          <div className="mb-3" key={lang}>
            <label className="form-label">
              <Typography variant="body-5">
                {i18n('tag.dimensions.add-member.label')} ({lang})
              </Typography>
            </label>
            <input
              type="text"
              className="form-control"
              value={labels[lang] ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setLabels((prev) => ({ ...prev, [lang]: value }));
              }}
              disabled={isLoading}
            />
          </div>
        ))}
        {!hasLabel && (
          <Typography variant="body-7" color="#717077">
            {i18n('tag.dimensions.add-member.label-required')}
          </Typography>
        )}
      </BootstrapModal.Body>
      <BootstrapModal.Footer>
        <button className="btn btn-secondary" onClick={handleClose} disabled={isLoading}>
          {i18n('common.cancel')}
        </button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
          {i18n(isEdit ? 'tag.dimensions.edit-member.submit' : 'tag.dimensions.add-member.submit')}
        </button>
      </BootstrapModal.Footer>
    </BootstrapModal>
  );
};
